import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { dashboardEntitiesService } from '../services/dashboardEntities.service';
import { DashboardEntity, DashboardEntityResponse } from '../services/types';
import { selectTenantIdsByNames } from './tenantsSlice';


// ---------- Types ----------
type EntityType = 'sit' | 'label' | 'policy';
type EntityStateKey = 'sits' | 'labels' | 'policies';

interface EntityState {
  entities: DashboardEntity[];
  previousPageEntities: DashboardEntity[]; 
  loading: boolean;
  error: string | null;
  retryCount: number;
  lastUpdated: string | null;
  // Pagination fields
  pageNumber: number;
  totalPages: number;
  hasNextPage: boolean;
  currentTenantIds: string[]; 
}

interface DashboardEntityState {
  sits: EntityState;
  labels: EntityState;
  policies: EntityState;
}

// Map entity types to state keys
const getStateKey = (entityType: EntityType): EntityStateKey => {
  switch (entityType) {
    case 'sit': return 'sits';
    case 'label': return 'labels';
    case 'policy': return 'policies';
    default: return 'sits';
  }
};

const createInitialEntityState = (): EntityState => ({
  entities: [],
  previousPageEntities: [],
  loading: false,
  error: null,
  retryCount: 0,
  lastUpdated: null,
  pageNumber: 1,
  totalPages: 0,
  hasNextPage: false,
  currentTenantIds: [],
});

const initialState: DashboardEntityState = {
  sits: createInitialEntityState(),
  labels: createInitialEntityState(),
  policies: createInitialEntityState(),
};

// ---------- Retry configuration ----------
const MAX_RETRIES = 3;
const RETRY_DELAYS = [2000, 4000, 6000]; // 2s, 4s, 6s

// ---------- Async Thunks ----------

// SITs thunk
export const fetchDashboardSits = createAsyncThunk<
  DashboardEntityResponse,
  { tenantIds: string[]; pageNumber?: number }
>('dashboardEntity/fetchSits', async ({ tenantIds, pageNumber = 1 }) => {
  const response = await dashboardEntitiesService.fetchEntities('sit', tenantIds, pageNumber);
  return response;
});

// Labels thunk  
export const fetchDashboardLabels = createAsyncThunk<
  DashboardEntityResponse,
  { tenantIds: string[]; pageNumber?: number }
>('dashboardEntity/fetchLabels', async ({ tenantIds, pageNumber = 1 }) => {
  const response = await dashboardEntitiesService.fetchEntities('label', tenantIds, pageNumber);
  return response;
});

// Policies thunk
export const fetchDashboardPolicies = createAsyncThunk<
  DashboardEntityResponse,
  { tenantIds: string[]; pageNumber?: number }
>('dashboardEntity/fetchPolicies', async ({ tenantIds, pageNumber = 1 }) => {
  const response = await dashboardEntitiesService.fetchEntities('policy', tenantIds, pageNumber);
  return response;
});

// ---------- Slice ----------
const dashboardEntitySlice = createSlice({
  name: 'dashboardEntity',
  initialState,
  reducers: {
    // SITs pagination actions
    setDashboardSitsPage: (state, action: PayloadAction<number>) => {
      state.sits.pageNumber = Math.max(1, action.payload);
    },
    clearDashboardSits: (state) => {
      state.sits = createInitialEntityState();
    },
    
    // Labels pagination actions  
    setDashboardLabelsPage: (state, action: PayloadAction<number>) => {
      state.labels.pageNumber = Math.max(1, action.payload);
    },
    clearDashboardLabels: (state) => {
      state.labels = createInitialEntityState();
    },
    
    // Policies pagination actions
    setDashboardPoliciesPage: (state, action: PayloadAction<number>) => {
      state.policies.pageNumber = Math.max(1, action.payload);
    },
    clearDashboardPolicies: (state) => {
      state.policies = createInitialEntityState();
    },
    
    // Clear all entities (existing functionality)
    clearAllEntities: (state) => {
      state.sits = createInitialEntityState();
      state.labels = createInitialEntityState();
      state.policies = createInitialEntityState();
    },
    clearEntityError: (state, action: PayloadAction<EntityType>) => {
      const entityType = action.payload;
      const stateKey = getStateKey(entityType);
      state[stateKey].error = null;
      state[stateKey].retryCount = 0;
    },
    resetRetryCount: (state, action: PayloadAction<EntityType>) => {
      const entityType = action.payload;
      const stateKey = getStateKey(entityType);
      state[stateKey].retryCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // SITs handlers
      .addCase(fetchDashboardSits.pending, (state) => {
        state.sits.loading = true;
        state.sits.error = null;
      })
      .addCase(fetchDashboardSits.fulfilled, (state, action) => {
        // Cache previous page for back navigation
        if (state.sits.entities.length > 0 && action.payload.pageNumber > 1) {
          state.sits.previousPageEntities = state.sits.entities;
        }
        
        state.sits.loading = false;
        state.sits.entities = action.payload.entities;
        state.sits.pageNumber = action.payload.pageNumber;
        state.sits.totalPages = action.payload.totalPages;
        state.sits.hasNextPage = action.payload.hasNextPage;
        state.sits.currentTenantIds = action.meta.arg.tenantIds;
        state.sits.lastUpdated = new Date().toISOString();
        state.sits.error = null;
        state.sits.retryCount = 0;
      })
      .addCase(fetchDashboardSits.rejected, (state, action) => {
        state.sits.loading = false;
        state.sits.error = action.error.message || 'Failed to load dashboard SITs';
        state.sits.retryCount += 1;
      })

      // Labels handlers
      .addCase(fetchDashboardLabels.pending, (state) => {
        state.labels.loading = true;
        state.labels.error = null;
      })
      .addCase(fetchDashboardLabels.fulfilled, (state, action) => {
        // Cache previous page for back navigation
        if (state.labels.entities.length > 0 && action.payload.pageNumber > 1) {
          state.labels.previousPageEntities = state.labels.entities;
        }
        
        state.labels.loading = false;
        state.labels.entities = action.payload.entities;
        state.labels.pageNumber = action.payload.pageNumber;
        state.labels.totalPages = action.payload.totalPages;
        state.labels.hasNextPage = action.payload.hasNextPage;
        state.labels.currentTenantIds = action.meta.arg.tenantIds;
        state.labels.lastUpdated = new Date().toISOString();
        state.labels.error = null;
        state.labels.retryCount = 0;
      })
      .addCase(fetchDashboardLabels.rejected, (state, action) => {
        state.labels.loading = false;
        state.labels.error = action.error.message || 'Failed to load dashboard Labels';
        state.labels.retryCount += 1;
      })

      // Policies handlers
      .addCase(fetchDashboardPolicies.pending, (state) => {
        state.policies.loading = true;
        state.policies.error = null;
      })
      .addCase(fetchDashboardPolicies.fulfilled, (state, action) => {
        // Cache previous page for back navigation
        if (state.policies.entities.length > 0 && action.payload.pageNumber > 1) {
          state.policies.previousPageEntities = state.policies.entities;
        }
        
        state.policies.loading = false;
        state.policies.entities = action.payload.entities;
        state.policies.pageNumber = action.payload.pageNumber;
        state.policies.totalPages = action.payload.totalPages;
        state.policies.hasNextPage = action.payload.hasNextPage;
        state.policies.currentTenantIds = action.meta.arg.tenantIds;
        state.policies.lastUpdated = new Date().toISOString();
        state.policies.error = null;
        state.policies.retryCount = 0;
      })
      .addCase(fetchDashboardPolicies.rejected, (state, action) => {
        state.policies.loading = false;
        state.policies.error = action.error.message || 'Failed to load dashboard Policies';
        state.policies.retryCount += 1;
      })
  },
});

export const { 
  setDashboardSitsPage,
  clearDashboardSits,
  setDashboardLabelsPage,
  clearDashboardLabels,
  setDashboardPoliciesPage,
  clearDashboardPolicies,
  clearAllEntities, 
  clearEntityError, 
  resetRetryCount 
} = dashboardEntitySlice.actions;
export default dashboardEntitySlice.reducer;

// ---------- Selectors ----------
export const selectDashboardEntities = (state: RootState, entityType: EntityType) => {
  const stateKey = getStateKey(entityType);
  return state.dashboardEntity[stateKey];
};

export const selectDashboardSits = (state: RootState) => state.dashboardEntity.sits;
export const selectDashboardLabels = (state: RootState) => state.dashboardEntity.labels;
export const selectDashboardPolicies = (state: RootState) => state.dashboardEntity.policies;